import {
  ArrowLeftIcon,
  BuildingOfficeIcon,
  BuildingStorefrontIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClipboardDocumentListIcon,
  CloudArrowUpIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  EyeSlashIcon,
  HomeIcon,
  IdentificationIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import classNames from '../../utils/classNames';
import { EXCLUDED_ROUTES } from '../../utils/mainModules';
import { ROUTES } from '../../utils/routes';
import { ROUTE_TITLES } from '../../utils/titles';

interface NavItem {
  name: string;
  href: string;
  icon: React.ForwardRefExoticComponent<React.SVGProps<SVGSVGElement>>;
}

// Group admin navigation items by category
const adminNavigation: { category: string; items: NavItem[] }[] = [
  {
    category: 'Entreprises',
    items: [
      {
        name: ROUTE_TITLES[ROUTES.ADMIN_COMPANIES_LIST],
        href: ROUTES.ADMIN_COMPANIES_LIST,
        icon: BuildingOfficeIcon,
      },
      {
        name: ROUTE_TITLES[ROUTES.ADMIN_CLIENTS_LIST],
        href: ROUTES.ADMIN_CLIENTS_LIST,
        icon: UserGroupIcon,
      },
      {
        name: ROUTE_TITLES[ROUTES.ADMIN_CLIENTS_PLANNING],
        href: ROUTES.ADMIN_CLIENTS_PLANNING,
        icon: CalendarDaysIcon,
      },
      {
        name: ROUTE_TITLES[ROUTES.ADMIN_CARD_HOLDERS_LIST],
        href: ROUTES.ADMIN_CARD_HOLDERS_LIST,
        icon: BuildingStorefrontIcon,
      },
      {
        name: ROUTE_TITLES[ROUTES.ADMIN_PENDING_CONFIG_IMPORTS],
        href: ROUTES.ADMIN_PENDING_CONFIG_IMPORTS,
        icon: CloudArrowUpIcon,
      },
    ],
  },
  {
    category: 'Imports',
    items: [
      {
        name: ROUTE_TITLES[ROUTES.ADMIN_SOFTWARE_IMPORT],
        href: ROUTES.ADMIN_SOFTWARE_IMPORT,
        icon: Cog6ToothIcon,
      },
      {
        name: ROUTE_TITLES[ROUTES.ADMIN_SCI_CONTACT_IMPORT],
        href: ROUTES.ADMIN_SCI_CONTACT_IMPORT,
        icon: IdentificationIcon,
      },
      {
        name: ROUTE_TITLES[ROUTES.ADMIN_IMPORT_HISTORY],
        href: ROUTES.ADMIN_IMPORT_HISTORY,
        icon: ClipboardDocumentListIcon,
      },
      {
        name: ROUTE_TITLES[ROUTES.ADMIN_SIRET_TRACKED_IMPORT],
        href: ROUTES.ADMIN_SIRET_TRACKED_IMPORT,
        icon: MagnifyingGlassIcon,
      },
    ],
  },
  {
    category: 'Copropriétés',
    items: [
      {
        name: ROUTE_TITLES[ROUTES.ADMIN_COMPANY_VERIFICATION],
        href: ROUTES.ADMIN_COMPANY_VERIFICATION,
        icon: CheckCircleIcon,
      },
      {
        name: ROUTE_TITLES[ROUTES.ADMIN_COPRO_VALIDATION],
        href: ROUTES.ADMIN_COPRO_VALIDATION,
        icon: CheckCircleIcon,
      },
    ],
  },
  {
    category: 'Contenu',
    items: [
      {
        name: ROUTE_TITLES[ROUTES.ADMIN_BLOG_POSTS],
        href: ROUTES.ADMIN_BLOG_POSTS,
        icon: DocumentTextIcon,
      },
      {
        name: ROUTE_TITLES[ROUTES.ADMIN_NEWSLETTER],
        href: ROUTES.ADMIN_NEWSLETTER,
        icon: EnvelopeIcon,
      },
    ],
  },
];

// Hidden pages - dynamically generated from EXCLUDED_ROUTES in mainModules
const hiddenPagesNavigation: NavItem[] = EXCLUDED_ROUTES
  .filter((route) => {
    // Filter out routes with parameters or that don't have titles
    return !route.includes(':') && 
           !route.includes('*') && 
           ROUTE_TITLES[route];
  })
  .map((route) => ({
    name: ROUTE_TITLES[route],
    href: route,
    icon: EyeSlashIcon,
  }));

const AdminSidebar: React.FC = () => {
  const location = useLocation();
  const [isHiddenPagesOpen, setIsHiddenPagesOpen] = useState(false);

  const isActive = (href: string) => {
    // Exact match or starts with (for detail pages)
    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  };

  return (
    <aside className="fixed top-0 left-0 z-40 hidden h-screen w-64 border-r border-slate-700 bg-rgpd-admin lg:block">
      <div className="flex h-full flex-col">
        {/* Logo / Header area */}
        <div className="flex h-16 items-center justify-between border-b border-slate-700 px-4">
          <span className="text-lg font-semibold text-white">
            Administration
          </span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {/* Back to frontend link */}
          <div className="mb-4">
            <Link
              to={ROUTES.HOME}
              className="flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:bg-slate-700 hover:text-white"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              Retour au tableau de bord
            </Link>
          </div>

          <div className="border-t border-slate-700 pt-4">
            {adminNavigation.map((group) => (
              <div key={group.category} className="mb-6">
                <h3 className="mb-2 px-3 text-xs font-semibold tracking-wider text-slate-400 uppercase">
                  {group.category}
                </h3>
                <ul className="space-y-1">
                  {group.items.map((item) => (
                    <li key={item.href}>
                      <Link
                        to={item.href}
                        className={classNames(
                          'flex items-center gap-x-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                          isActive(item.href)
                            ? 'bg-slate-700 text-white'
                            : 'text-slate-300 hover:bg-slate-700/50 hover:text-white',
                        )}
                      >
                        <item.icon className="h-5 w-5 flex-shrink-0" />
                        <span className="truncate">{item.name}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Hidden Pages - Collapsable Section */}
          <div className="mt-4 border-t border-slate-700 pt-4">
            <button
              onClick={() => setIsHiddenPagesOpen(!isHiddenPagesOpen)}
              className="flex w-full items-center justify-between px-3 py-2 text-xs font-semibold tracking-wider text-slate-400 uppercase transition-colors hover:text-slate-200"
            >
              <span className="flex items-center gap-2">
                <EyeSlashIcon className="h-4 w-4" />
                Pages masquées
              </span>
              {isHiddenPagesOpen ? (
                <ChevronDownIcon className="h-4 w-4" />
              ) : (
                <ChevronRightIcon className="h-4 w-4" />
              )}
            </button>
            
            {isHiddenPagesOpen && (
              <ul className="mt-2 space-y-1">
                {hiddenPagesNavigation.map((item) => (
                  <li key={item.href}>
                    <Link
                      to={item.href}
                      className={classNames(
                        'flex items-center gap-x-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                        isActive(item.href)
                          ? 'bg-slate-700 text-white'
                          : 'text-slate-300 hover:bg-slate-700/50 hover:text-white',
                      )}
                    >
                      <item.icon className="h-5 w-5 flex-shrink-0" />
                      <span className="truncate">{item.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </nav>

        {/* Footer */}
        <div className="border-t border-slate-700 p-4">
          <Link
            to={ROUTES.HOME}
            className="flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:bg-slate-700 hover:text-white"
          >
            <HomeIcon className="h-5 w-5" />
            <span>Accueil</span>
          </Link>
        </div>
      </div>
    </aside>
  );
};

export default AdminSidebar;
